import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
  IconButton,
  Tooltip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { 
  deleteTdsDeclaration, 
  searchTdsDeclarations,
  updateTdsDeclaration 
} from "../../../Services/Services";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import TdsDeclarationForm from "./TdsDeclarationForm";
import Popover from "@mui/material/Popover";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// Edit Dialog Component
const EditTdsDeclarationDialog = ({ open, onClose, declaration, onSave }) => {
  const [formData, setFormData] = useState({
    truck_no: "",
    truck_owner: "",
    financial_year: "2025-26",
    description: "",
  });

  const financialYearOptions = [
    "2024-25",
    "2025-26",
    "2026-27",
    "2027-28",
    "2028-29",
  ];

  useEffect(() => {
    if (declaration) {
      setFormData({
        truck_no: declaration.truck_no || "",
        truck_owner: declaration.truck_owner || "",
        financial_year: declaration.financial_year || "2025-26",
        description: declaration.description || "",
      });
    }
  }, [declaration]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'truck_no' ? value.toUpperCase() : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(declaration.tds_id, formData);
      onClose();
    } catch (error) {
      console.error("Error updating declaration:", error);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit TDS Declaration</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Truck Number *"
                name="truck_no"
                value={formData.truck_no}
                onChange={handleChange}
                required
                size="small"
                placeholder="e.g., MH12AB1234"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Truck Owner *"
                name="truck_owner"
                value={formData.truck_owner}
                onChange={handleChange}
                required
                size="small"
                placeholder="Enter owner name"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Financial Year"
                name="financial_year"
                value={formData.financial_year}
                onChange={handleChange}
                size="small"
                SelectProps={{
                  native: true,
                }}
              >
                {financialYearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
                size="small"
                placeholder="Additional details..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="error">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Update
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default function TdsDeclarationList() {
  const [declarations, setDeclarations] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [selectedForEdit, setSelectedForEdit] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedForDelete, setSelectedForDelete] = useState(null);
  const [search, setSearch] = useState({
    truck_no: "",
    truck_owner: "",
    financial_year: "",
  });
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState({});
  const tableContainerRef = useRef(null);
  const observer = useRef();

  const pageSize = 50; // Number of records per page

  // Debounce effect for search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page when search changes
      setDeclarations([]); // Clear existing data
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  // Initial fetch and fetch when search changes
  useEffect(() => {
    fetchDeclarations(true);
  }, [debouncedSearch]);

  const fetchDeclarations = async (reset = false) => {
    if (loading) return;
    
    setLoading(true);
    try {
      const currentPage = reset ? 1 : page;
      const params = {
        page: currentPage,
        page_size: pageSize,
        ...(debouncedSearch.truck_no && { truck_no: debouncedSearch.truck_no }),
        ...(debouncedSearch.truck_owner && { truck_owner: debouncedSearch.truck_owner }),
        ...(debouncedSearch.financial_year && { financial_year: debouncedSearch.financial_year }),
      };

      const response = await searchTdsDeclarations(params);
      
      if (reset) {
        setDeclarations(response.results || response);
      } else {
        setDeclarations(prev => [...prev, ...(response.results || response)]);
      }
      
      setTotalCount(response.count || (response.results || response).length);
      setHasMore((response.results || response).length === pageSize);
      
      if (!reset) {
        setPage(prev => prev + 1);
      } else {
        setPage(2);
      }
    } catch (error) {
      console.error("Error fetching TDS declarations:", error);
    } finally {
      setLoading(false);
    }
  };

  // Infinite scroll observer
  const lastRowElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchDeclarations();
      }
    });
    
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  const handleDelete = async (tdsId) => {
    try {
      await deleteTdsDeclaration(tdsId);
      // Refresh data after delete
      setPage(1);
      setDeclarations([]);
      fetchDeclarations(true);
    } catch (error) {
      console.error("Error deleting declaration:", error);
    }
  };

  const handleUpdate = async (tdsId, data) => {
    try {
      await updateTdsDeclaration(tdsId, data);
      setPage(1);
      setDeclarations([]);
      fetchDeclarations(true);
    } catch (error) {
      console.error("Error updating declaration:", error);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearch(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleClearSearch = () => {
    setSearch({
      truck_no: "",
      truck_owner: "",
      financial_year: "",
    });
    setDebouncedSearch({});
  };

  const handleEditClick = (declaration) => {
    setSelectedForEdit(declaration);
    setOpenEditModal(true);
  };

  const handlePrintHTML = () => {
    const originalTable = document.getElementById("tds-declaration-table");
    if (!originalTable) return;

    const clonedTable = originalTable.cloneNode(true);
    const removeIndexes = new Set();
    const headerRow = clonedTable.querySelector("thead tr");

    if (headerRow) {
      headerRow.querySelectorAll("th").forEach((th, idx) => {
        if (
          th.classList.contains("no-print") ||
          th.innerText.toLowerCase().includes("actions")
        ) {
          removeIndexes.add(idx);
        }
      });
    }

    clonedTable.querySelectorAll("tr").forEach((row) => {
      const cells = Array.from(row.children);
      for (let i = cells.length - 1; i >= 0; i--) {
        const cell = cells[i];
        if (
          removeIndexes.has(i) ||
          cell.classList.contains("no-print")
        ) {
          cell.remove();
        }
      }
    });

    const printContent = clonedTable.outerHTML;
    const printWindow = window.open("", "", "width=1000,height=600");

    printWindow.document.write(`
    <html>
    <head>
      <style>
        body {
          font-family: Arial, sans-serif;
          padding: 0px 20px;
        }
        header {
          text-align: center;
          font-size: 20px;
          font-weight: bold;
          margin-bottom: 20px;
          color: #333;
        }
        footer {
          position: fixed;
          bottom: 10px;
          width: 100%;
          text-align: center;
          font-size: 12px;
          color: #333;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th, td {
          border: 1px solid black;
          padding: 8px;
          text-align: center;
          font-size: 12px;
          white-space: nowrap;
        }
        th {
          background-color: rgba(211, 211, 211, 0.42);
          color: #000;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        .no-print {
          display: none !important;
        }
        @media print {
          html, body {
            zoom: 90%;
          }
        }
      </style>
    </head>
    <body>
      <header>SR ENTERPRICES</header>
      <h3 style="text-align: center;">TDS Declaration List</h3>
      <p>Total Records: ${totalCount}</p>
      ${printContent}
      <footer>Powered by : YASHVITECH IT SOLUTION PVT LTD</footer>
      <script>
        window.onload = function() {
          window.print();
          window.onafterprint = function() {
            window.close();
          };
        };
      </script>
    </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.focus();
  };

  const handleExportExcel = () => {
    const headers = [
      "Sr. No",
      "Truck Number",
      "Truck Owner",
      "Financial Year",
      "Description",
      "Created At"
    ];

    const worksheetData = [headers];

    declarations.forEach((declaration, index) => {
      const row = [
        index + 1,
        declaration.truck_no || "-",
        declaration.truck_owner || "-",
        declaration.financial_year || "-",
        declaration.description || "-",
        declaration.created_at 
          ? new Date(declaration.created_at).toLocaleDateString('en-IN')
          : "-"
      ];
      worksheetData.push(row);
    });

    if (!worksheetData.length) {
      alert("No valid data found to export.");
      return;
    }

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    const columnWidths = headers.map((_, colIndex) => {
      const maxLength = worksheetData.reduce((max, row) => {
        const cell = row[colIndex];
        const len = cell ? cell.toString().length : 10;
        return Math.max(max, len);
      }, headers[colIndex].length);
      return { wch: maxLength + 2 };
    });
    worksheet["!cols"] = columnWidths;

    const blackBorder = {
      top: { style: "thin", color: { rgb: "000000" } },
      bottom: { style: "thin", color: { rgb: "000000" } },
      left: { style: "thin", color: { rgb: "000000" } },
      right: { style: "thin", color: { rgb: "000000" } },
    };

    headers.forEach((_, colIdx) => {
      const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: colIdx })];
      if (cell) {
        cell.s = {
          fill: { fgColor: { rgb: "FFFF99" } },
          font: { bold: true, color: { rgb: "000000" } },
          alignment: { horizontal: "center", vertical: "center" },
          border: blackBorder,
        };
      }
    });

    for (let r = 1; r < worksheetData.length; r++) {
      for (let c = 0; c < headers.length; c++) {
        const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
        if (cell) {
          cell.s = {
            alignment: { horizontal: "center", vertical: "center" },
            border: blackBorder,
          };
        }
      }
    }

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "TDS Declarations");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, `TDS_Declarations_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box sx={{ width: "100%", maxHeight: "calc(100vh - 70px)", p: 0 }}>
      {/* Search and Action Buttons in Single Line */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "nowrap",
          overflowX: "auto",
          position: "sticky",
          top: 10,
          zIndex: 1000,
          backgroundColor: "transparent",
          p: 0,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={10}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap", overflowX: "auto", pb: 1 }}>
              <TextField
                // label="Truck No"
                name="truck_no"
                value={search.truck_no}
                onChange={handleSearchChange}
                size="small"
                sx={{ minWidth: 180 }}
                placeholder="Search truck number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                // label="Truck Owner"
                name="truck_owner"
                value={search.truck_owner}
                onChange={handleSearchChange}
                size="small"
                sx={{ minWidth: 180 }}
                placeholder="Search owner name"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                // label="Financial Year"
                name="financial_year"
                value={search.financial_year}
                onChange={handleSearchChange}
                size="small"
                sx={{ minWidth: 180 }}
                placeholder="e.g., 2025-26"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              {(search.truck_no || search.truck_owner || search.financial_year) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearSearch}
                  sx={{ height: 40 }}
                >
                  Clear
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Typography variant="caption" color="textSecondary" sx={{ mr: 1, alignSelf: 'center' }}>
                Total: {totalCount}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenAddModal(true)}
                size="small"
                sx={{ minWidth: 100 }}
              >
                Add TDS
              </Button>
              <Tooltip title="Export to Excel">
                <Button
                  variant="outlined"
                  onClick={handleExportExcel}
                  size="small"
                  sx={{ minWidth: 40, height: 40, p: 0 }}
                >
                  <FileDownloadIcon fontSize="small" />
                </Button>
              </Tooltip>
              <Tooltip title="Print">
                <Button
                  variant="outlined"
                  onClick={handlePrintHTML}
                  size="small"
                  sx={{ minWidth: 40, height: 40, p: 0 }}
                >
                  <PrintIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Table Container with Infinite Scroll */}
      <TableContainer
        ref={tableContainerRef}
        sx={{
          maxHeight: "75vh",
          overflow: "auto",
          width: "100%",
          p: 0,
          m: 0,
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px"
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1"
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555"
          }
        }}
      >
        <Table
          stickyHeader
          size="small"
          id="tds-declaration-table"
          sx={{
            minWidth: "100%",
            tableLayout: "auto",
            whiteSpace: "nowrap",
            "& td, & th": {
              borderBottom: "1px solid #ccc",
              textAlign: "center",
              whiteSpace: "nowrap",
              padding: "8px 12px",
              fontSize: "0.75rem"
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                "& th": {
                  fontWeight: "bold",
                  borderBottom: "1px solid #999",
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableCell>Sr. No</TableCell>
              <TableCell>Truck Number</TableCell>
              <TableCell>Truck Owner</TableCell>
              <TableCell>Financial Year</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="center" className="no-print">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {declarations.map((declaration, index) => (
              <TableRow
                key={declaration.tds_id}
                ref={index === declarations.length - 1 ? lastRowElementRef : null}
                sx={{
                  backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>{index + 1}</TableCell>
                <TableCell>{declaration.truck_no || "-"}</TableCell>
                <TableCell>{declaration.truck_owner || "-"}</TableCell>
                <TableCell>{declaration.financial_year || "-"}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 200,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Tooltip
                    arrow
                    placement="top"
                    title={declaration.description || "No description"}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        maxWidth: "200px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                      }}
                    >
                      {declaration.description || "-"}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {declaration.created_at 
                    ? new Date(declaration.created_at).toLocaleDateString('en-IN')
                    : "-"}
                </TableCell>
                <TableCell align="center" className="no-print">
                  <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditClick(declaration)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setSelectedForDelete(declaration.tds_id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            
            {/* Loading indicator for infinite scroll */}
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    Loading more records...
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            
            {!loading && declarations.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No TDS declarations found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add TDS Declaration Dialog */}
      <Dialog
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Add TDS Declaration</DialogTitle>
        <DialogContent dividers>
          <TdsDeclarationForm 
            setDeclarations={() => {
              fetchDeclarations(true);
            }} 
            setOpenAddModal={setOpenAddModal} 
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddModal(false)} color="error">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit TDS Declaration Dialog */}
      <EditTdsDeclarationDialog
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedForEdit(null);
        }}
        declaration={selectedForEdit}
        onSave={handleUpdate}
      />

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setSelectedForDelete(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" gutterBottom>
            Are you sure you want to delete this TDS declaration?
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setSelectedForDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              color="error"
              size="small"
              variant="contained"
              onClick={async () => {
                try {
                  await handleDelete(selectedForDelete);
                } finally {
                  setAnchorEl(null);
                  setSelectedForDelete(null);
                }
              }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}